//@dependencies
import PropTypes from "prop-types";
//local imports
import { getFormattedDate } from "../../../../../../../Utils/utils";
//styles
import styles from "./NotificationText.module.scss";
import { Grid2 } from "@mui/material";
import { ReactComponent as Sites } from "../../../../../../../GlobalAssets/svg/Sites.svg";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ConstructionIcon from "@mui/icons-material/Construction";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { ReactComponent as WafIcon } from "../.././../../../../../Features/Admin/AdminAssets/svg/WafIcon.svg";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import StorageIcon from "@mui/icons-material/Storage";
import SpeedIcon from "@mui/icons-material/Speed";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";

// bells for site jobs that finish in the background: header → icon (the header's
// last words say how the job went, see getSiteJobStatus)
const SITE_JOB_ICONS = [
  [/^backup (started|created|failed)$/, BackupOutlinedIcon],
  [/^restore (started|completed|failed)$/, SettingsBackupRestoreIcon],
  [/^wordpress (update started|updated|update failed)$/, SystemUpdateAltIcon],
  [/^storage upgrade (started|completed|failed|needs attention)$/, StorageIcon],
  [
    /^performance upgrade (started|completed|failed|needs attention)$/,
    SpeedIcon,
  ],
  [/^site (deletion started|deleted|deletion failed)$/, DeleteOutlineIcon],
  // v2 custom domains with AWS-issued SSL
  [/^custom domain (setup started|going live|is live|setup failed|removed|needs attention)$/, HttpsOutlinedIcon],
  [/^primary domain changed$/, HttpsOutlinedIcon],
];

const getSiteJobStatus = (lowerCaseHeader) => {
  if (/failed$/.test(lowerCaseHeader)) return "statusError";
  if (/needs attention$/.test(lowerCaseHeader)) return "statusWarning";
  if (/(started|going live)$/.test(lowerCaseHeader)) return "statusProgress";
  return "statusSuccess";
};

const NotificationText = ({
  body,
  createdAt,
  header,
  onClick,
  onMarkAsRead,
  isSelected,
  ...rest
}) => {
  let time = null;
  if (createdAt) {
    time = new Date(createdAt);
  }

  const lowerCaseHeader = header.toLowerCase().trim();
  const siteJobIcon = SITE_JOB_ICONS.find(([pattern]) =>
    pattern.test(lowerCaseHeader),
  )?.[1];
  let iconStatus = siteJobIcon ? getSiteJobStatus(lowerCaseHeader) : null;
  if (lowerCaseHeader === "site creation" && /fail/i.test(body)) {
    iconStatus = "statusError";
  }

  const formatBody = (body, lowerCaseHeader) => {
    if (
      lowerCaseHeader === "site update" &&
      /deleted successfully|added successfully/.test(body)
    ) {
      const words = body.split(" ");
      words[2] = `<b style="color: #135FC2;">${words[2]}</b>`;
      body = words.join(" ");
    }

    if (lowerCaseHeader === "site creation" && body.includes("Your site")) {
      const words = body.split(" ");
      words[2] = `<b style="color: #135FC2;">${words[2]}</b>`;
      body = words.join(" ");
    }

    if (
      lowerCaseHeader === "organization update" &&
      /created successfully|deleted successfully/.test(body)
    ) {
      const words = body.split(" ");
      words[0] = `<b style="color: #135FC2;">${words[0]}</b>`;
      body = words.join(" ");
    }

    if (
      lowerCaseHeader === "organization update" &&
      body.includes("invited to the organization")
    ) {
      const words = body.split(" ");
      const lastIndex = words.length - 1;
      words[lastIndex] = `<b style="color: #135FC2;">${words[lastIndex]}</b>`;
      body = words.join(" ");
    }

    if (
      /on-demand backup created|on-demand backup creation/.test(
        lowerCaseHeader,
      ) &&
      body.includes("On-demand Backup has been created")
    ) {
      const words = body.split(" ");
      const lastIndex = words.length - 3;
      words[lastIndex] = `<b style="color: #135FC2;">${words[lastIndex]}</b>`;
      body = words.join(" ");
    }

    if (
      /restore initiated/.test(lowerCaseHeader) &&
      body.includes("A Restore has been initiated")
    ) {
      const words = body.split(" ");
      const lastIndex = words.length - 3;
      words[lastIndex] = `<b style="color: #135FC2;">${words[lastIndex]}</b>`;
      body = words.join(" ");
    }

    if (
      lowerCaseHeader === "blueprint creation" &&
      body.includes("created Blueprint")
    ) {
      const blueprintNameRegex = /\b[a-zA-Z0-9_-]+\b(?=, is live now)/;
      body = body.replace(
        blueprintNameRegex,
        (match) => `<b style="color: #135FC2;">${match}</b>`,
      );
    }

    if (
      lowerCaseHeader === "your site is live now" &&
      body.includes("created Blueprint")
    ) {
      const blueprintNameRegex = /\b[a-zA-Z0-9_-]+\b(?=, is live now)/;
      body = body.replace(
        blueprintNameRegex,
        (match) => `<b style="color: #135FC2;">${match}</b>`,
      );
    }

    if (
      lowerCaseHeader === "otp verification" &&
      body.includes("Your verification code")
    ) {
      const codeRegex = /\b\d{4}\b/;
      const orgNameRegex = /\b[a-zA-Z0-9_-]+\b(?= ,this expires in)/;
      body = body.replace(
        codeRegex,
        (match) => `<b style="color: #135FC2;">${match}</b>`,
      );
      body = body.replace(
        orgNameRegex,
        (match) => `<b style="color: #135FC2;">${match}</b>`,
      );
    }

    if (
      lowerCaseHeader === "site update" &&
      body.includes("associated with the certificate")
    ) {
      const domainRegex = /\b(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}\b/;
      body = body.replace(
        domainRegex,
        (match) => `<b style="color: #135FC2;">${match}</b>`,
      );
    }

    if (
      lowerCaseHeader === "waf update" &&
      body.toLowerCase().includes("waf has been")
    ) {
      const words = body.split(" ");
      const siteName = words[2].replace(/'s$/, "");
      const highlightedSiteName = `<b style="color: #135FC2;">${siteName}</b>'s`;
      words[2] = highlightedSiteName;
      body = words.join(" ");
    }

    // site jobs: "X has been restored", "We're updating X to ...", "... added to X."
    if (siteJobIcon) {
      const leadingSiteRegex = /^[a-zA-Z0-9_.-]+(?= (?:has been|is being|was|is) )/;
      const siteAfterRegex =
        /(\b(?:backup of|restore of|restoring|updating|update|deleting|deletion of|your site|for|to) )([a-zA-Z0-9_.-]*[a-zA-Z0-9_-])/i;
      if (leadingSiteRegex.test(body) && !/^(your|the|we)\b/i.test(body)) {
        body = body.replace(
          leadingSiteRegex,
          (match) => `<b style="color: #135FC2;">${match}</b>`,
        );
      } else {
        body = body.replace(
          siteAfterRegex,
          (match, lead, siteName) =>
            `${lead}<b style="color: #135FC2;">${siteName}</b>`,
        );
      }
    }

    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    return body.replace(
      emailRegex,
      (email) => `<b style="color: #135FC2;">${email}</b>`,
    );
  };

  const renderIcon = () => {
    if (siteJobIcon) {
      const SiteJobIcon = siteJobIcon;
      return <SiteJobIcon className={styles.colorIcon} />;
    }
    switch (lowerCaseHeader) {
      case "site update":
        return <Sites className={styles.colorIcon} />;
      case "your site is live now":
        return <Sites className={styles.colorIcon} />;
      case "site creation":
        return <ConstructionIcon className={styles.colorIcon} />;
      case "organization update":
        return <CorporateFareIcon className={styles.colorIcon} />;
      case "on-demand backup created":
        return <VpnLockIcon className={styles.colorIcon} />;
      case "on-demand backup creation":
        return <VpnLockIcon className={styles.colorIcon} />;
      case "on-demand backup failed":
        return <VpnLockIcon className={styles.colorIcon} />;
      case "on-demand backup initiated":
        return <VpnLockIcon className={styles.colorIcon} />;
      case "on-demand backup success":
        return <VpnLockIcon className={styles.colorIcon} />;
      case "blueprint creation":
        return <DrawOutlinedIcon className={styles.colorIcon} />;
      case "otp verification":
        return <MoveUpIcon className={styles.colorIcon} />;
      case "restore initiated":
        return <SettingsBackupRestoreIcon className={styles.colorIcon} />;
      case "waf update":
        return <WafIcon className={styles.colorIcon} />;
      case "staging addon expired":
        return <DeviceThermostatIcon className={styles.colorIcon} />;
      default:
        if (/on-demand backup/.test(body.toLowerCase())) {
          return <VpnLockIcon className={styles.colorIcon} />;
        }
        return <NotificationsNoneOutlinedIcon className={styles.colorIcon} />;
    }
  };

  return (
    <Grid2 className={styles.invitation} onClick={onClick} {...rest}>
      <Grid2
        className={`${styles.icon} ${iconStatus ? styles[iconStatus] : ""}`}
      >
        {renderIcon()}
      </Grid2>
      <Grid2 className={styles.invite}>
        <span
          dangerouslySetInnerHTML={{
            __html: formatBody(body, lowerCaseHeader),
          }}
        />
        <Grid2 className={styles.formattedDate}>
          {time !== null ? getFormattedDate(time) : ""}
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

NotificationText.propTypes = {
  body: PropTypes.string.isRequired,
  createdAt: PropTypes.string,
  header: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onMarkAsRead: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default NotificationText;
